

import React, { useState, useEffect, useRef, useCallback } from "react";
import { message, Modal } from "antd";
import AIReportUpload from "../Quantum/AIReportUpload";
import ReportUploadProgress from "../Quantum/ReportUploadProgress";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../PageRouting/AppRoutes";
import { startGlobalUpload } from "../../auth/utils/globalUploader";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const log = (event, payload) => {
  try { console.log("[SkinCareUpload]", event, payload || ""); } catch {}
};
const resolveFileType = (selectedFile) => {
  const mimeToExt = (m) => (m === "image/png" ? ".png" : (m === "image/jpeg" || m === "image/jpg") ? ".jpg" : "");
  const extFromName = selectedFile?.name?.includes(".") ? "." + selectedFile.name.split(".").pop().toLowerCase() : "";
  const ct = selectedFile?.type || "application/octet-stream";
  return extFromName || mimeToExt(ct);
};

export default function SkinCareUpload({ patientId,sourcechannel, createdBy, serviceName = "Skin Care", onStarted, initialVisible, onCloseInitial }) {
  const [file, setFile] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [uploadUrls, setUploadUrls] = useState([]);
  const [stage, setStage] = useState(initialVisible ? "instructions" : null);
  const [progress, setProgress] = useState(0);
  const [errorText, setErrorText] = useState("");

  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraError, setCameraError] = useState("");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const endpointBase = process.env.REACT_APP_QUANTUMURL;

  useEffect(() => {
    if (initialVisible) {
      setStage("instructions");
      log("initial_modal_open");
    }
  }, [initialVisible]);

  // Start camera
  const startCamera = useCallback(async (mode = facingMode) => {
    setCameraError("");
    setCapturedPhoto(null);
    try {
      if (cameraStream) {
        cameraStream.getTracks().forEach(t => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setCameraError("Camera access denied or not available. Please allow camera permission.");
      log("camera_error", String(err));
    }
  }, [cameraStream, facingMode]);

  // Assign stream to video element when both are ready
  useEffect(() => {
    if (showCamera && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [showCamera, cameraStream]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  const openCamera = () => {
    setShowCamera(true);
    setCapturedPhoto(null);
    startCamera(facingMode);
  };

  const closeCamera = () => {
    stopCamera();
    setShowCamera(false);
    setCapturedPhoto(null);
    setCameraError("");
  };

  const flipCamera = () => {
    const newMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newMode);
    startCamera(newMode);
  };

  // Capture photo from video stream
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setCapturedPhoto(dataUrl);
    stopCamera();
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera(facingMode);
  };

  // Convert dataURL to File and process
  const confirmCapturedPhoto = () => {
    if (!capturedPhoto) return;
    fetch(capturedPhoto)
      .then(r => r.blob())
      .then(blob => {
        const f = new File([blob], "captured.jpg", { type: "image/jpeg" });
        closeCamera();
        beforeUpload(f);
      });
  };

  const generateUploadUrl = async (ct, selectedFile) => {
    setProgress(10);
    try { localStorage.setItem("quantumUploadSession", JSON.stringify({ requestId: null, serviceName, progress: 10, checkedOnce: false })); } catch {}
    setStage("ai");
    log("ai_modal_open");
    const resolvedFileType = resolveFileType(selectedFile);
    const payload = {
      patientId,
      serviceName,
      files: [{ fileNumber: 1, fileType: resolvedFileType, contentType: ct }]
    };
    try {
      log("prepare_upload_start", payload);
      await delay(10000);
      const url = new URL("new_generate-resumable-url", endpointBase).toString();
      const res = await axios.post(url, payload, { headers: { "Content-Type": "application/json" } });
      const data = res.data;
      const rid = data?.request_id || data?.requestId || data?.requestid || data?.requestID || null;
      setRequestId(rid);
      setUploadUrls(data?.upload_urls || []);
      setProgress(50);
      try {
        const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
        localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, requestId: rid, serviceName, progress: 50 }));
      } catch {}
      setStage("progress");
      setErrorText("");
      log("prepare_upload_success", { requestId: rid });
      return { rid, urls: data?.upload_urls || [] };
    } catch (err) {
      setErrorText(String(err));
      message.error("Failed to prepare upload. Please try again.");
      setStage("ai");
      log("prepare_upload_error", { message: String(err) });
      localStorage.removeItem("quantumUploadSession");
      return Promise.reject(err);
    }
  };

  const uploadFile = async (urlsParam, fileParam) => {
    const urls = Array.isArray(urlsParam) ? urlsParam : uploadUrls;
    const useFile = fileParam || file;
    if (!urls.length || !useFile) return;
    setStage("progress");
    const ct = useFile?.type || "application/octet-stream";
    try {
      await delay(10000);
      await axios.put(urls[0].url, useFile, {
        headers: { "Content-Type": ct },
        timeout: 60000,
        onUploadProgress: (evt) => {
          if (evt?.loaded && evt?.total > 0) {
            const pct = 50 + Math.min(30, Math.round((evt.loaded / evt.total) * 30));
            setProgress(pct);
            try {
              const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
              localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, progress: pct }));
            } catch {}
          }
        },
      });
      setProgress(80);
      setErrorText("");
    } catch (err) {
      setErrorText(String(err));
      localStorage.removeItem("quantumUploadSession");
      throw err;
    }
  };

  const checkUploaded = async (ridOverride) => {
    const rid = ridOverride || requestId;
    if (!rid) return;
    setProgress((p) => (p < 90 ? 90 : p));
    const url = new URL("check-uploaded-files", endpointBase).toString();
    
    const body = { 
        requestId: rid, 
        serviceName, 
        sourceChannel: sourcechannel,  
        createdBy: createdBy,          
        signedUrlCount: 1, 
        fileType: resolveFileType(file) 
    };
    try {
      await delay(10000);
      const res = await axios.post(url, body, { headers: { "Content-Type": "application/json", accept: "application/json" } });
      if (res.data) {
        setProgress(100);
        setErrorText("");
      } else {
        setErrorText("Upload verification failed.");
        message.error("Upload verification failed.");
      }
    } catch (err) {
      setErrorText(String(err));
      message.error("Failed to verify upload.");
      localStorage.removeItem("quantumUploadSession");
      throw err;
    }
  };

  const beforeUpload = (f) => {
  const isImage = f.type === "image/png" || f.type === "image/jpeg" || f.type === "image/jpg";
  if (!isImage) { setErrorText("Only PNG, JPG, or JPEG images are allowed!"); message.error("Only PNG, JPG, or JPEG images are allowed!"); return false; }
  const isLt5MB = f.size / 1024 / 1024 < 5;
  if (!isLt5MB) { setErrorText("Image must be smaller than 5MB!"); message.error("Image must be smaller than 5MB!"); return false; }
  setErrorText("");
  localStorage.removeItem("quantumUploadSession");

  const reader = new FileReader();
  reader.onload = () => { setUploadedImage(reader.result); };
  reader.readAsDataURL(f);
  setFile(f);
  log("file_selected", { name: f?.name, type: f?.type, size: f?.size });
  // Stay on instructions modal to show preview; upload starts on "Get Report" click
  return false;
  };

  const handleGetReport = () => {
    if (!file) return;
    setStage("ai");
    if (typeof onStarted === "function") onStarted();
    startGlobalUpload({
      file,
      serviceName,
      patientId,
      sourcechannel, createdBy,
      endpointBase: process.env.REACT_APP_QUANTUMURL,
      onProgress: (pct) => {
        setProgress(pct);
        if (pct >= 50) setStage("progress");
      },
      onSuccess: () => {
        setProgress(100);
        setStage("progress");
      },
      onError: () => {
        setProgress(0);
        setStage("ai");
        setErrorText("Upload failed. Please try again.");
        localStorage.removeItem("quantumUploadSession");
      },
    });
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) beforeUpload(f);
    e.target.value = "";
  };

  return (
    <>
      {/* ── INSTRUCTIONS MODAL ── */}
      <Modal
        open={stage === "instructions"}
        onCancel={() => {
          setStage(null);
          setErrorText("");
          if (typeof onCloseInitial === "function") onCloseInitial();
        }}
        footer={null}
        centered
        width={600}
        style={{ maxWidth: '100vw' }}
        title={
  <div
    style={{
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      fontSize: '30px',
      lineHeight: '110%', // slightly more for 2 lines
      letterSpacing: '0.3%',
      color: 'var(--color-brand-primary)',
      maxWidth: '420px', // ✅ forces wrap into 2 lines
      whiteSpace: 'normal', // ✅ allow wrapping
      paddingBottom: '6px'
    }}
  >
    Upload photos & get an instant result
  </div>
}
       >
        <div className="flex flex-col md:flex-row gap-6 mt-4">
          <ul className="list-disc text-xs md:text-sm text-[#004276] space-y-2 pl-5 flex-1">
            {[
              "Ensure good lighting",
              "Hold the camera steady and avoid motion blur.",
              "Focus clearly on the affected skin area.",
              "Remove makeup, creams, or filters from the area.",
              "Avoid shadows or overexposure on the skin.",
              "Upload only one skin condition per image."
            ].map((text, idx) => <li key={idx}>{text}</li>)}
          </ul>
          <div className="flex flex-col items-center gap-2 flex-1">
            {uploadedImage ? (
              <img src={uploadedImage} alt="Uploaded" className="h-48 w-48 rounded object-cover" />
            ) : (
              <div className="h-48 w-48 bg-gray-200 rounded flex flex-col items-center justify-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a4 4 0 014-4h10a4 4 0 014 4v10a4 4 0 01-4 4H7a4 4 0 01-4-4V7z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 13l2.5 3 3.5-4.5M21 15l-5.586-5.586M3 3l18 18" />
                </svg>
                <span className="text-xs mt-2">No Image</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex gap-3 justify-center flex-wrap">
          {/* Choose from files */}
          <label style={{ padding: "8px 20px", backgroundColor: file ? "#6B7280" : "#004276", color: "#ffffff", fontSize: "14px", fontWeight: 600, borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
            {file ? "Change Photo" : "Choose File"}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
          </label>

          {/* Open live camera */}
          {!file && (
            <button
              style={{ padding: "8px 20px", backgroundColor: "#004276", color: "#ffffff", fontSize: "14px", fontWeight: 600, borderRadius: "6px", cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}
              onClick={openCamera}
            >
              📷 Open Camera
            </button>
          )}

          {/* Get Report — shown only after image is selected */}
          {file && (
            <button
              style={{ padding: "8px 28px", backgroundColor: "#004276", color: "#ffffff", fontSize: "14px", fontWeight: 600, borderRadius: "999px", cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}
              onClick={handleGetReport}
            >
              Get Report
            </button>
          )}
        </div>
        {errorText && (
          <p style={{ color: "#FA0505", fontSize: "13px", textAlign: "center", marginTop: "10px" }}>{errorText}</p>
        )}
      </Modal>

      {/* ── CAMERA MODAL ── */}
      <Modal
        open={showCamera}
        onCancel={closeCamera}
        footer={null}
        centered
        width={560}
        style={{ maxWidth: '100vw' }}
        title="Take a Photo"
        destroyOnClose
      >
        <div className="flex flex-col items-center gap-4">
          {cameraError ? (
            <div className="text-red-500 text-sm text-center p-4">{cameraError}</div>
          ) : capturedPhoto ? (
            /* Show captured photo for confirmation */
            <>
              <img src={capturedPhoto} alt="Captured" className="w-full max-h-72 object-contain rounded-lg border" />
              <div className="flex gap-3 w-full justify-center">
                <button
                  style={{ padding: "8px 20px", backgroundColor: "#6B7280", color: "#ffffff", fontSize: "14px", fontWeight: 600, borderRadius: "999px", cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}
                  onClick={retakePhoto}
                >
                  🔄 Retake
                </button>
                <button
                  style={{ padding: "8px 20px", backgroundColor: "#004276", color: "#ffffff", fontSize: "14px", fontWeight: 600, borderRadius: "999px", cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}
                  onClick={confirmCapturedPhoto}
                >
                  ✅ Use This Photo
                </button>
              </div>
            </>
          ) : (
            /* Live camera preview */
            <>
              <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '4/3' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex gap-3 justify-center w-full">
                {/* Flip camera */}
                <button
                  style={{ padding: "8px 20px", backgroundColor: "#6B7280", color: "#ffffff", fontSize: "14px", fontWeight: 600, borderRadius: "999px", cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}
                  onClick={flipCamera}
                >
                  🔃 Flip
                </button>
                {/* Capture */}
                <button
                  style={{ padding: "8px 20px", backgroundColor: "#004276", color: "#ffffff", fontSize: "14px", fontWeight: 600, borderRadius: "999px", cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}
                  onClick={capturePhoto}
                >
                  📸 Capture
                </button>
              </div>
              <p className="text-xs text-gray-400 text-center">Position the affected skin area in frame, then click Capture</p>
            </>
          )}
        </div>
        {/* Hidden canvas for capturing frames */}
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </Modal>

      {/* Upload button outside modal */}
      {stage !== "instructions" && (
        <div className="flex gap-2 mt-1 flex-wrap">
          <label className="px-3 py-1 bg-[#004276] text-white text-xs md:text-sm rounded cursor-pointer">
            Upload
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
          </label>
          <button
            className="px-3 py-1 bg-[#004276] text-white text-xs md:text-sm rounded cursor-pointer border-none"
            onClick={openCamera}
          >
            📷 Camera
          </button>
        </div>
      )}

      <AIReportUpload
        solutionType='skin_care'
        visible={stage === "ai"}
        customHeaderTitle={errorText ? "Image upload failed !" : undefined}
        customHeaderColor={errorText ? "#FA0505" : undefined}
        customTitle={errorText ? "Oops, we couldn't upload your image. Please check your connection and try uploading again so our AI can generate your report." : undefined}
        customDescription={errorText ? "Your image upload was unsuccessful-please upload the photo again !" : undefined}
        customDescriptionColor={errorText ? "#FA0505" : undefined}
        customButtonText={errorText ? "Upload Again" : undefined}
        onCancel={() => {
          setStage(null);
          setErrorText("");
        }}
       onExplore={() => {
  if (errorText) { setStage("instructions"); setErrorText(""); return; }
  try {
    const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
    localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, checkedOnce: true }));
  } catch {}
  navigate("/");
}}
        onWait={() => { setStage("progress"); }}
      />

      <ReportUploadProgress
        solutionType='skin_care'
        visible={stage === "progress"}
        onCancel={() => { setStage(null); }}
        progress={progress}
        imageSrc={progress >= 80 && uploadedImage ? uploadedImage : undefined}
        title={errorText ? "Upload failed" : undefined}
        description={errorText ? errorText : undefined}
        analyzingText={errorText ? "Please close and retry" : undefined}
        onView={() => {
          
          navigate("/aireports", { state: { tab: "skin_care" } });
          localStorage.removeItem("quantumUploadSession");
        }}
      />
    </>
  );
} 